import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/16/solid";
import { CheckIcon } from "@heroicons/react/20/solid";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useContext, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCreateMutation } from "../../api/genericHooks";
import { BookContext } from "../../contexts/BookContext";
import { selectUser } from "../../store/slices/auth";
import { Language, Locale } from "../../types/models/Language";
import { NarrationStyle } from "../../types/models/NarrationStyle";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
};

type GenerateBookAIData = {
  author: string;
  narration_style: NarrationStyle;
  language: Language;
};

export const GenerateBookAIModal: FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const locale = (localStorage.getItem("i18nextLng") || "fr") as Locale;
  const user = useSelector(selectUser);
  const { book, narration_styles, languages, refresh } =
    useContext(BookContext);

  const isDraft = book?.status === "draft";

  // Form default values ======================================================
  const defaultValues = useMemo<GenerateBookAIData>(() => {
    return {
      author: [user?.first_name, user?.last_name].filter(Boolean).join(" "),
      narration_style:
        narration_styles.find((ns) => ns.id === book?.narration_style_id) ||
        narration_styles[0],
      language:
        languages.find((language) => language.id === book?.language_id) ||
        languages[0],
    };
  }, [user, book, narration_styles, languages]);

  // Form Methods and State ===================================================
  const GenerateAIFormSchema = Yup.object().shape({
    author: Yup.string()
      .required(t("errors.required"))
      .max(26, t("errors.max", { max: 26 })),
    narration_style: Yup.mixed<NarrationStyle>().required(t("errors.required")),
    language: Yup.mixed<Language>().required(t("errors.required")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<GenerateBookAIData>({
    defaultValues: defaultValues,
    resolver: yupResolver(GenerateAIFormSchema),
  });

  const { narration_style } = watch();

  // Mutations ===============================================================
  const generateBookAIMutation = useCreateMutation("/generate_book_ai");

  // Form Submit =============================================================
  const onSubmit = (values: GenerateBookAIData) => {
    const data = {
      book: {
        language_id: values.language.id,
        narration_style_id: values.narration_style.id,
        author: values.author,
      },
    };
    generateBookAIMutation.mutateAsync(data).then(() => {
      toast.success(t("generateBookAIModal.sentToGenerationSuccess"));
      refresh();
      setOpen(false);
    });
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            {isDraft ? (
              <div className="flex items-center justify-center">
                <p className="text-lg font-semibold text-slate-800">
                  {t("generateBookAIModal.tooltip")}
                </p>
              </div>
            ) : (
              <form
                className="bg-white flex flex-1 flex-col justify-between"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="w-5 h-5 sm:w-6 sm:h-6"
                    />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                    <DialogTitle
                      as="h3"
                      className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                    >
                      {t("generateBookAIModal.title")}
                    </DialogTitle>
                    <hr className="mt-2 w-full border-gray-200" />
                    <div className="mt-5">
                      <div className="mt-5 mx-auto">
                        <div className="border-b border-gray-900/10 pb-12">
                          <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                              <label
                                htmlFor="author"
                                className="block text-sm/6 font-medium text-slate-900"
                              >
                                {t("generateBookAIModal.author")}
                              </label>
                              <div className="mt-2">
                                <input
                                  id="author"
                                  type="text"
                                  {...register("author", { required: true })}
                                  autoComplete="author"
                                  maxLength={26}
                                  className="block w-full rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600 sm:text-sm/6"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-3">
                              <label
                                htmlFor="language"
                                className="block text-sm/6 font-medium text-slate-900"
                              >
                                {t("generateBookAIModal.language")}
                              </label>
                              <div className="mt-2">
                                <Controller
                                  name="language"
                                  control={control}
                                  render={({ field }) => (
                                    <Listbox
                                      value={field.value}
                                      onChange={field.onChange}
                                    >
                                      <div className="relative mt-2">
                                        <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded bg-white py-1.5 pl-3 pr-2 text-left text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600 sm:text-sm/6">
                                          <span className="col-start-1 row-start-1 flex items-center gap-3 pr-6">
                                            <span className="block truncate">
                                              {field.value?.emoji_flag}
                                              <span className="ml-3">
                                                {field.value?.name}
                                              </span>
                                            </span>
                                          </span>
                                          <ChevronUpDownIcon
                                            aria-hidden="true"
                                            className="col-start-1 row-start-1 size-5 self-center justify-self-end text-slate-500 sm:size-4"
                                          />
                                        </ListboxButton>

                                        <ListboxOptions
                                          transition
                                          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                        >
                                          {languages &&
                                            languages.map((language) => (
                                              <ListboxOption
                                                key={language.id}
                                                value={language}
                                                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-slate-900 data-[focus]:bg-delicate-600 data-[focus]:text-white data-[focus]:outline-none"
                                              >
                                                <div className="flex items-center">
                                                  <span className="block truncate font-normal group-data-[selected]:font-semibold">
                                                    {language.emoji_flag}
                                                    <span className="ml-3">
                                                      {language.name}
                                                    </span>
                                                  </span>
                                                </div>

                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-delicate-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                                                  <CheckIcon
                                                    aria-hidden="true"
                                                    className="size-5"
                                                  />
                                                </span>
                                              </ListboxOption>
                                            ))}
                                        </ListboxOptions>
                                      </div>
                                    </Listbox>
                                  )}
                                />
                                {errors.language && (
                                  <p
                                    className="text-sm text-red-600"
                                    role="alert"
                                  >
                                    {t("errors.required")}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="sm:col-span-6">
                              <label
                                htmlFor="narration_style"
                                className="block text-sm/6 font-medium text-slate-900"
                              >
                                {t("generateBookAIModal.narration_style")}
                              </label>
                              <div className="mt-2">
                                <Controller
                                  name="narration_style"
                                  control={control}
                                  render={({ field }) => (
                                    <Listbox
                                      value={field.value}
                                      onChange={field.onChange}
                                    >
                                      <div className="relative mt-2">
                                        <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded bg-white py-1.5 pl-3 pr-2 text-left text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600 sm:text-sm/6">
                                          <span className="col-start-1 row-start-1 flex items-center gap-3 pr-6">
                                            <span className="block truncate">
                                              <span className="ml-3">
                                                {field.value?.name[locale]}
                                              </span>
                                            </span>
                                          </span>
                                          <ChevronUpDownIcon
                                            aria-hidden="true"
                                            className="col-start-1 row-start-1 size-5 self-center justify-self-end text-slate-500 sm:size-4"
                                          />
                                        </ListboxButton>

                                        <ListboxOptions
                                          transition
                                          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                        >
                                          {narration_styles &&
                                            narration_styles.map(
                                              (narration_style) => (
                                                <ListboxOption
                                                  key={narration_style.id}
                                                  value={narration_style}
                                                  className="group relative cursor-default select-none py-2 pl-3 pr-9 text-slate-900 data-[focus]:bg-delicate-600 data-[focus]:text-white data-[focus]:outline-none"
                                                >
                                                  <div className="flex items-center">
                                                    <span className="block truncate font-normal group-data-[selected]:font-semibold">
                                                      <span className="ml-3">
                                                        {
                                                          narration_style.name[
                                                            locale
                                                          ]
                                                        }
                                                      </span>
                                                    </span>
                                                  </div>

                                                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-delicate-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                                                    <CheckIcon
                                                      aria-hidden="true"
                                                      className="size-5"
                                                    />
                                                  </span>
                                                </ListboxOption>
                                              )
                                            )}
                                        </ListboxOptions>
                                      </div>
                                    </Listbox>
                                  )}
                                />
                                {errors.narration_style && (
                                  <p
                                    className="text-sm text-red-600"
                                    role="alert"
                                  >
                                    {t("errors.required")}
                                  </p>
                                )}
                                <div>
                                  {narration_style && (
                                    <>
                                      <p className="text-sm text-slate-800 mt-6 font-semibold">
                                        {t("generateBookAIModal.example")} :{" "}
                                        <br />
                                      </p>
                                      <p className="text-sm text-slate-500 text-justify mt-2">
                                        {narration_style?.example[locale]}
                                      </p>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="sm:col-span-6">
                              <div className="rounded bg-delicate-50 p-4">
                                <div className="flex">
                                  <div className="shrink-0">
                                    <ExclamationTriangleIcon
                                      aria-hidden="true"
                                      className="size-5 text-delicate-900"
                                    />
                                  </div>
                                  <div className="ml-3">
                                    <h3 className="text-sm font-medium text-delicate-900">
                                      {t("generateBookAIModal.attention")}
                                    </h3>
                                    <div className="mt-2 text-sm text-slate-800">
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: t(
                                            "generateBookAIModal.attention2",
                                            {
                                              interpolation: {
                                                escapeValue: false,
                                              },
                                            }
                                          ),
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                  <button
                    type="submit"
                    className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                  >
                    {t("addPicturesModal.save")}
                  </button>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
